/* The Teams section (designs/teams-and-products.md TP8) — Admin only. The
   ONE team list intake speaks, curated here instead of on intake's old
   Manage teams tab: every team's name and type (platform, product, other —
   or not recorded, for a team a requester added on the form), the review
   queue those additions land in, retire, and merge.

   A team owns products — a product or other team gets its first one
   automatically, a platform team only when an Admin adds one (its market, vehicles and
   PDP live on the Products section); giving a team that type makes the
   product. Every change is an appended revision — nothing is overwritten.

   Not the Org section: Org is the Entra org chart, people and who they
   report to. This list is who registers, owns and backs intake efforts. */
import { useCallback, useEffect, useState, type FormEvent, type JSX } from "react";
import {
  TEAM_GROUPS,
  TEAM_KINDS,
  TEAM_KIND_LABELS,
  hasProduct,
  type CanonicalTeam,
  type TeamKind,
} from "../../../shared/canonical-teams";
import type { Product } from "../../../shared/products";
import type { Capability } from "../../../shared/capabilities";
import { ViewFrame } from "../shell/ViewFrame";
import { Loading } from "../shell/Loading";
import { LoadError } from "../shell/LoadError";
import { useAppState } from "../shell/store";
import { asJson, getProducts, getTeams, mergeTeam, postTeam, putTeam } from "../intake/api";

/* the hover help for each control, in one place (help-layer.md HL1) */
export const TEAM_ADMIN_HELP = {
  type:
    "Platform team: builds shared capability; may own platform products (PRAK, a Mobius product), added on Products. " +
    "Product team: one team per PDP, a market " +
    "product line (Dozer, Telehandler, Ag). Other team: a customer program or prototype work (Blitz, " +
    "Scythe). Not recorded: added from the intake form and waiting for you to say which.",
  review:
    "Added by a requester on the intake form. Say its type (or mark it reviewed) once you have checked " +
    "it is a real team and not a variant of one already listed — if it is a variant, merge it.",
  merge:
    "Moves everything that names this team — the efforts it filed, owners lists, its +1s, and its products " +
    "with their PDPs and vehicles — onto the team you pick, then retires this one. History keeps the old name.",
  retire: "Hides the team from new efforts. Its existing efforts keep it; bring it back any time.",
  product:
    "The products this team owns — each a PDP track with its own market and vehicles, on the Products section. " +
    "A team may own several; an Admin adds more with New product… there.",
} as const;

type Filter = "all" | "review";

export function TeamAdminView(): JSX.Element {
  const { state, store } = useAppState();
  const [teams, setTeams] = useState<CanonicalTeam[] | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [filed, setFiled] = useState<Map<string, number>>(new Map());
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [showRetired, setShowRetired] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async (): Promise<void> => {
    try {
      /* all three or nothing: "not made yet" and a zero count drive
         curation, so a failed read is the page's error (Copilot on #147) */
      const [t, p, caps] = await Promise.all([
        getTeams(),
        getProducts(),
        fetch("/api/intake/capabilities").then((r) => asJson<Capability[]>(r)),
      ]);
      setTeams(t);
      setProducts(p);
      setFiled(
        caps
          .filter((c) => c.archived !== true)
          .reduce((m, c) => m.set(c.teamId, (m.get(c.teamId) ?? 0) + 1), new Map<string, number>()),
      );
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => void load(), [load]);

  if (!state.me.roles.includes("admin"))
    return (
      <ViewFrame>
        <p className="form-error" role="alert">
          Teams is for Admins. Everyone sees the teams through the intake form and the queue.
        </p>
      </ViewFrame>
    );
  if (failed) return <LoadError what="teams" />;
  if (teams === null) return <Loading what="teams" />;

  /* true when the action landed — a caller keeps its input on a refusal */
  const run = async (act: () => Promise<unknown>, done?: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await act();
      await load();
      if (done) setNotice(done);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const live = teams.filter((t) => !t.retired);
  const reviewCount = live.filter((t) => t.needsReview).length;
  const q = search.trim().toLowerCase();
  const shown = teams
    .filter((t) => showRetired || !t.retired)
    .filter((t) => filter === "all" || t.needsReview)
    .filter((t) => !q || t.name.toLowerCase().includes(q));
  /* every product the team owns, retired included — "none yet" would be
     false for a team whose products are only retired (Copilot on #147) */
  const productsOf = (id: string): Product[] => products.filter((p) => p.teamId === id);

  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>Teams.</b>
          <span>
            {live.length} team{live.length === 1 ? "" : "s"} intake speaks — who registers, owns and backs an
            effort.{" "}
            {reviewCount > 0 ? (
              <span className="state unconfirmed">{reviewCount} waiting for review</span>
            ) : (
              "Nothing waiting for review."
            )}
          </span>
        </div>
      }
      footnote={
        <span>
          Admins only. People and who they report to are on <b>Org</b>; a product team's market, vehicles and PDP
          are on <b>Products</b>. Every change is a new revision — history keeps the old value.
        </span>
      }
    >
      {error !== null && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && <p className="form-notice">{notice}</p>}
      <AddTeam busy={busy} onAdd={(name, kind) => run(() => postTeam({ name, kind }), `Added ${name}.`)} />
      <div className="dash">
        <div className="dash-top">
          <label className="dash-search">
            <span className="sr-only">Search teams</span>
            <input
              type="search"
              autoComplete="off"
              placeholder="Search teams…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="field" title={TEAM_ADMIN_HELP.review}>
            <input
              type="checkbox"
              checked={filter === "review"}
              onChange={(e) => setFilter(e.target.checked ? "review" : "all")}
            />{" "}
            Waiting for review only
          </label>
          <label className="field" title={TEAM_ADMIN_HELP.retire}>
            <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Show
            retired
          </label>
        </div>
        <div className="dash-table">
          <table className="recs" data-team-admin>
            <thead>
              <tr>
                <th>Name</th>
                <th title={TEAM_ADMIN_HELP.type}>Type</th>
                <th title={TEAM_ADMIN_HELP.product}>Products</th>
                <th title="Live efforts this team registered.">Efforts</th>
                <th>Status</th>
                <th title={TEAM_ADMIN_HELP.merge}>Merge into</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td className="recs-empty" colSpan={6}>
                    {filter === "review" ? "Nothing is waiting for review." : "Nothing matches — clear the search."}
                  </td>
                </tr>
              ) : (
                TEAM_GROUPS.flatMap((g) => {
                  const rows = shown.filter((t) => t.kind === g.kind);
                  if (rows.length === 0) return [];
                  return [
                    <tr key={`g-${g.label}`} className="recs-group">
                      <th colSpan={6}>{g.label}</th>
                    </tr>,
                    ...rows.map((t) => (
                      <TeamRow
                        key={t.id}
                        team={t}
                        teams={live}
                        products={productsOf(t.id)}
                        efforts={filed.get(t.id) ?? 0}
                        busy={busy}
                        onOpenProduct={(id) => {
                          /* load the section, then open the record on it */
                          store.selectNav("products");
                          store.openProduct(id);
                        }}
                        run={run}
                      />
                    )),
                  ];
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ViewFrame>
  );
}

function AddTeam({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (name: string, kind: TeamKind | null) => Promise<boolean>;
}): JSX.Element {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<TeamKind | "">("");
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (name.trim() === "") return;
    /* a refused add keeps what was typed, so it can be corrected */
    if (!(await onAdd(name.trim(), kind === "" ? null : kind))) return;
    setName("");
    setKind("");
  };
  return (
    <form className="card-form" onSubmit={(e) => void submit(e)} data-add-team>
      <label className="field">
        New team{" "}
        <input maxLength={200} value={name} onChange={(e) => setName(e.target.value)} placeholder="Team name" />
      </label>
      <label className="field" title={TEAM_ADMIN_HELP.type}>
        Type{" "}
        <select value={kind} onChange={(e) => setKind(e.target.value as TeamKind | "")}>
          <option value="">Not recorded</option>
          {TEAM_KINDS.map((k) => (
            <option key={k} value={k}>
              {TEAM_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </label>
      <div className="record-actions">
        <button className="btn primary" type="submit" disabled={busy || name.trim() === ""}>
          Add team
        </button>
      </div>
    </form>
  );
}

function TeamRow({
  team,
  teams,
  products,
  efforts,
  busy,
  onOpenProduct,
  run,
}: {
  team: CanonicalTeam;
  teams: CanonicalTeam[];
  products: Product[];
  efforts: number;
  busy: boolean;
  onOpenProduct: (id: string) => void;
  run: (act: () => Promise<unknown>, done?: string) => Promise<boolean>;
}): JSX.Element {
  const [into, setInto] = useState("");
  const merged = team.mergedInto ? teams.find((t) => t.id === team.mergedInto)?.name ?? team.mergedInto : null;
  const merge = (): void => {
    const target = teams.find((t) => t.id === into);
    if (!target) return;
    if (
      !window.confirm(
        `Merge ${team.name} into ${target.name}? Its efforts, owners, +1s and vehicles move to ${target.name}, ` +
          `and ${team.name} is retired. History keeps the old name.`,
      )
    )
      return;
    void run(async () => {
      const r = await mergeTeam(team.id, target.id);
      return r;
    }, `Merged ${team.name} into ${target.name}.`);
  };
  return (
    <tr className={team.retired ? "rec row-retired" : "rec"} data-team-row={team.id}>
      <td>
        <input
          key={team.name}
          className="cell-edit"
          defaultValue={team.name}
          aria-label={`Rename ${team.name}`}
          disabled={busy || team.retired}
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next !== "" && next !== team.name) void run(() => putTeam(team.id, { name: next }));
          }}
        />
        <div className="recs-sub">{team.id}</div>
      </td>
      <td>
        <select
          aria-label={`Type for ${team.name}`}
          title={TEAM_ADMIN_HELP.type}
          value={team.kind ?? ""}
          disabled={busy || team.retired}
          onChange={(e) =>
            void run(() => putTeam(team.id, { kind: e.target.value === "" ? null : (e.target.value as TeamKind) }))
          }
        >
          <option value="">Not recorded</option>
          {TEAM_KINDS.map((k) => (
            <option key={k} value={k}>
              {TEAM_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </td>
      <td>
        {products.length > 0 ? (
          products.map((p, i) => (
            <span key={p.id}>
              {i > 0 && ", "}
              <button className="rec-name" onClick={() => onOpenProduct(p.id)} title={TEAM_ADMIN_HELP.product}>
                {p.name}
              </button>
              {p.retired && <span className="state retired">retired</span>}
            </span>
          ))
        ) : hasProduct(team.kind) ? (
          <span className="state missing">none yet</span>
        ) : (
          "—"
        )}
      </td>
      <td>{efforts}</td>
      <td>
        {team.needsReview && !team.retired && (
          <>
            <span className="state unconfirmed" title={TEAM_ADMIN_HELP.review}>
              needs review
            </span>{" "}
            <button
              className="btn ghost"
              disabled={busy}
              title="Keep it as it is: a real team, not a variant of one listed."
              onClick={() => void run(() => putTeam(team.id, { needsReview: false }))}
            >
              Mark reviewed
            </button>
          </>
        )}
        {merged !== null && <span className="state retired">merged into {merged}</span>}
        {team.retired && merged === null && <span className="state retired">retired</span>}{" "}
        {!team.mergedInto && (
          <button
            className="btn ghost"
            disabled={busy}
            title={TEAM_ADMIN_HELP.retire}
            onClick={() => void run(() => putTeam(team.id, { retired: !team.retired }))}
          >
            {team.retired ? "Bring back" : "Retire"}
          </button>
        )}
      </td>
      <td>
        {!team.retired && (
          <>
            <select
              aria-label={`Merge ${team.name} into`}
              title={TEAM_ADMIN_HELP.merge}
              value={into}
              disabled={busy}
              onChange={(e) => setInto(e.target.value)}
            >
              <option value="">—</option>
              {teams
                .filter((t) => t.id !== team.id)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>{" "}
            <button className="btn ghost" disabled={busy || into === ""} onClick={merge}>
              Merge
            </button>
          </>
        )}
      </td>
    </tr>
  );
}
